import React from 'react';
import {AccordionItem} from '@/components/molecules/AccordionItem';
import {useRewards} from '@/hooks/useRewards';
import {DISABLE_EDIT_REWARD_STATUS} from '@/utils/constants/rewardsConstants';
import {
  SetupSection,
  DisplaySection,
  AttributesSection,
  EditButton
} from './components';
import './rewardReview.scss';

import PropTypes from 'prop-types';

/**
 * RewardReview component - Displays a comprehensive review of reward configuration
 * across Setup, Display, and Attributes sections.
 *
 * @param {Object} props - Component props
 * @param {string} [props.mode='create'] - Flow mode: 'create', 'edit', or 'view'
 * @returns {JSX.Element} The rendered RewardReview component.
 */
const RewardReview = ({mode = 'create'}) => {
  const {formData, languages, setStep} = useRewards();

  const normalizedStatus = formData?.status?.toUpperCase?.();
  const isEditDisabled =
    !!normalizedStatus && DISABLE_EDIT_REWARD_STATUS.includes(normalizedStatus);
  const isViewMode = mode === 'view';

  const handleEditClick = (phaseNumber) => {
    if (!isViewMode) {
      setStep(phaseNumber);
    }
  };

  const renderEditButton = (phaseNumber) => {
    if (isEditDisabled || isViewMode) return null;
    return <EditButton phaseNumber={phaseNumber} onClick={handleEditClick} />;
  };

  return (
    <div className='reward-review-container mt-4 flex flex-col gap-6'>
      <AccordionItem
        isDark
        expanded
        label='Setup'
        size='small'
        rightAccessoryChild={renderEditButton(1)}
        content={<SetupSection formData={formData} />}
      />

      <AccordionItem
        isDark
        expanded
        label='Display'
        size='small'
        rightAccessoryChild={renderEditButton(2)}
        content={<DisplaySection formData={formData} languages={languages} />}
      />

      <AccordionItem
        isDark
        expanded
        label='Attributes'
        size='small'
        rightAccessoryChild={renderEditButton(3)}
        content={<AttributesSection formData={formData} />}
      />
    </div>
  );
};

RewardReview.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit', 'view'])
};

export default RewardReview;
